import { useState } from 'react';
import { Skeleton } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { WizardFooter, WizardIntro } from '../../ui';
import { useLegalTexts } from '../api';
import { LegalTextList } from '../LegalParts';
import { LoadError } from '../LoadError';
import type { LegalTexts } from '../types';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import styles from './steps.module.css';

/** The sentence under the cards: the API's example hint while a current text is still an example (D-333). */
function exampleNote(texts: LegalTexts): string | null {
  return texts.banner === null ? null : `${texts.banner.title} ${texts.banner.text}`;
}

/** Onboarding Admin 05 · Rechtstexte: four cards, the inline editor with the counter, PDF upload. */
export function LegalTextsStep() {
  const nav = useWizardNav();
  const texts = useLegalTexts();
  const [editing, setEditing] = useState<string | null>(null);
  const [pending, setPending] = useState<'later' | 'next' | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const withdrawalMissing = texts.data?.items.find((item) => item.type === 'withdrawal')?.source === null;

  async function next(kind: 'later' | 'next') {
    setPending(kind);
    setStepError(null);
    try {
      await nav.complete('legal_texts', 'continue', 'domain');
    } catch (error) {
      setStepError(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  const note = texts.data ? exampleNote(texts.data) : null;
  return (
    <>
      <WizardIntro
        eyebrow={stepEyebrow('legal-texts')}
        title="Rechtstexte"
        textWidth={640}
        text="DAMOKLES verlinkt diese Texte im Mandantenportal und in Terminbestätigungen. Hinterlegt sind Beispieltexte — ersetzen Sie sie durch Ihre eigenen Texte oder laden Sie PDFs hoch."
      />
      {texts.isPending ? (
        <div className={styles.skeletonStack}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="block" height={78} />
          ))}
        </div>
      ) : texts.isError ? (
        <LoadError
          what="Die Rechtstexte konnten nicht geladen werden"
          error={texts.error}
          retrying={texts.isFetching}
          onRetry={() => void texts.refetch()}
        />
      ) : (
        <LegalTextList texts={texts.data} variant="wizard" editing={editing} onEdit={setEditing} />
      )}
      {note ? <div className={styles.infoBox}>{note}</div> : null}
      {stepError ? (
        <div className={styles.error} role="alert">
          {stepError}
        </div>
      ) : null}
      <WizardFooter
        back={{ label: 'Zurück', onClick: () => nav.go('company') }}
        secondary={
          withdrawalMissing
            ? { label: 'Widerruf später', loading: pending === 'later', onClick: () => void next('later') }
            : undefined
        }
        primary={{ label: 'Weiter', loading: pending === 'next', onClick: () => void next('next') }}
      />
    </>
  );
}
